/**
 * `<FormDialog>` — slot-based modal chrome built on monks-ui `Dialog*`
 * primitives. Used by form-style dialogs that need consistent header,
 * scrollable body, footer with primary/secondary actions, ARIA wiring,
 * focus trap (inherited from Radix), and notistack-safe outside dismissal.
 *
 * The component is intentionally thin: it owns chrome and dismissal
 * semantics. Form state, validation, and submission live in the consumer.
 *
 * The slots, the footer contract and the notistack guard are shared with
 * `<FormSheet>` — see `formShell.tsx`. Which of the two a screen uses is a
 * chrome decision: destructive confirmations stay dialogs, create and edit
 * forms are sheets.
 */
import clsx from 'clsx';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@monksflow/monks-ui';
import type { FormDialogProps } from './FormDialog.types';
import { FormShellBody, FormShellFooter } from './formShell';
import { FORM_SHELL_BODY_INSET, FormShellBodyInsetContext } from './formShell.context';
import { findSlots, guardNotistackDismissal } from './formShell.utils';

const FormDialogRoot = ({
  open,
  onOpenChange,
  title,
  'data-testid': dataTestId,
  description,
  children,
  scrollable,
  size,
  className,
  onOpenAutoFocus,
}: FormDialogProps) => {
  const { body, footer } = findSlots(children);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={dataTestId}
        // Passing the attribute at all overrides the id Radix would point at
        // its own description, so only opt out when there is none to link.
        {...(description ? {} : { 'aria-describedby': undefined })}
        scrollable={scrollable}
        {...(size ? { size } : {})}
        // Each body below adds `2xs` of ring room above and below itself, so the gap around it
        // closes by the same step and the rows keep the design system's own spacing; with no
        // footer below it, the panel's bottom padding closes by that step instead. Unscrolled,
        // the panel hands its horizontal padding to its rows the way `scrollable` does, so a
        // body that scrolls itself spans the panel and holds its controls inside its clip.
        className={clsx(
          body && 'gap-sm',
          body && !footer && 'pb-lg',
          !scrollable &&
            'px-0 [&>[data-slot=dialog-header]]:px-xl [&>[data-slot=dialog-footer]]:px-xl',
          className,
        )}
        onPointerDownOutside={guardNotistackDismissal}
        onOpenAutoFocus={onOpenAutoFocus}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        {/* `scrollable` drops the panel to `px-0` and restores the inset on its
            three slots by `data-slot`, so a body that is not `DialogBody` keeps
            neither the 24px nor the overflow that makes it the scroll box; this
            slot then only needs the vertical ring room inside its clip.
            Otherwise the wrapper takes no box (`contents`), so the Body slot's own
            classes lay it out against the panel: a body sized `min-h-0 flex-1` gets
            a bounded height and its list scrolls, instead of the whole panel. */}
        {body && (
          <DialogBody data-testid="form-dialog-body" className={scrollable ? 'py-2xs' : 'contents'}>
            {scrollable ? (
              body
            ) : (
              <FormShellBodyInsetContext.Provider value={FORM_SHELL_BODY_INSET}>
                {body}
              </FormShellBodyInsetContext.Provider>
            )}
          </DialogBody>
        )}
        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Public compound component. Consumers only ever import `FormDialog` from
 * this module and access slots via `FormDialog.Body` / `FormDialog.Footer`
 * — keeps the import surface tight and the slot identity stable.
 */
export const FormDialog = Object.assign(FormDialogRoot, {
  Body: FormShellBody,
  Footer: FormShellFooter,
});
